import { Component, Input, OnInit } from '@angular/core';
import { TranslateService }         from '@ngx-translate/core';
import { UIRouter }                 from '@uirouter/core';

import { BookingModel }                      from './typings/bookings.booking';
import { BookingsAppointmentTypeModel }      from './typings/bookings.appointment-type';
import { BookingsRequestActionsComponent }   from './bookings.request.actions.component';
import { BookingsRequestModel }              from './typings/bookings.request';
import { BookingsRequestSendEmailComponent } from './bookings.request.send-email.component';
import { BookingsRequestSendSmsComponent }   from './bookings.request.send-sms.component';
import { BookingsService }                   from './bookings.service';
import { CompanyMessagesModel }              from '../companies/models/company.messages';
import { DateParserService }                 from '../core/services/date-parser.service';
import { MessagesService }                   from '../messages/messages.service';
import { MessengerService }                  from '../messages/messenger/messenger.service';
import { SessionService }                    from '../sessions/session.service';
import { Table }                             from '../table/types/table';
import { TableQueryParameters }              from '../table/types/table-query-parameters';
import { TableService }                      from '../table/table.service';

@Component({
  selector   : 'wor-bookings-requests',
  template: require('./bookings.requests.component.html')
})
export class BookingsRequestsComponent implements OnInit {
  @Input() appointmentTypes: Array<BookingsAppointmentTypeModel> = [];
  @Input() companyMessages : CompanyMessagesModel;
  @Input() requests        : Array<BookingsRequestModel> = [];

  tableCacheKey = this.bookingsService.requestTableCacheKey;

  tableSettings: Table = {
    cache: {
      enabled: true,
      key    : this.tableCacheKey
    },
    columns: [
      {
        field : 'requested_date',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.REQUESTED_DATE')
        },
        pipe: 'globalDate',
      },
      {
        field : 'appointmentTypes',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.BOOKING_TYPES')
        }
      },
      {
        field : 'customer_last_name',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.LAST_NAME')
        }
      },
      {
        field : 'customer_first_name',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.FIRST_NAME')
        }
      },
      {
        field : 'customer_phone',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.MOBILE')
        }
      },
      {
        field : 'source',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.SOURCE')
        },
        pipe: 'properCase',
      },
      {
        component: {
          bindings: {
            outputs: {
              onEditClick  : request => this.select(request),
              onDeleteClick: request => this.delete(request)
            }
          },
          enabled: true,
          factory: BookingsRequestActionsComponent,
        },
        sortable: true,
      }
    ],
    empty: {
      text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_BOOKING_REQUESTS_FOUND')
    },
    pagination: {
      enabled   : true,
      pageSize  : this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
      pageSizes : [ 10, 20, 50 ],
      queryCount: () => this.bookingsService.getRequestsCount()
    },
    rows: {
      highlight: {
        enabled: true
      }
    },
    sorting: this.tableService.setSortingFromCache(this.tableCacheKey, {
      direction: 'desc',
      enabled  : true,
      field    : 'requested_date'
    })
  };

  constructor (
    private bookingsService  : BookingsService,
    private dateParserService: DateParserService,
    private messagesService  : MessagesService,
    private messengerService : MessengerService,
    private router           : UIRouter,
    private sessionService   : SessionService,
    private tableService     : TableService,
    private translateService : TranslateService,
  ) {}

  ngOnInit () : void {
    this.writeAppointmentTypes();
  }

  delete ( request : BookingsRequestModel ) : void {
    this.bookingsService.deleteRequest(request)
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.REQUEST_DELETED') , 'success');

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error));
  }

  get ( params : TableQueryParameters ) : Promise<Array<BookingsRequestModel>> {
    return this.bookingsService.getRequests(params)
      .then(( requests : Array<BookingsRequestModel> ) => {
        this.writeAppointmentTypes(requests);

        this.requests = requests;

        return requests;
      });
  }

  refresh () : void {
    this.router.stateService.reload();

    this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.REFRESHED') , 'success');
  }

  select ( request : BookingsRequestModel ) : void {
    let booking : BookingModel;

    this.bookingsService.viewRequest(request)
    .then(( data : BookingModel ) => {
      booking = data;

      if ( booking.deleted ) {
        return Promise.resolve(booking.request);
      }

      return this.bookingsService.updateRequest(booking.request);
    })
    .then(( request : BookingsRequestModel ) => {
      booking.request = request;

      if ( booking.skipApproval ) {
        return Promise.resolve();
      }

      return booking.request.customer_preferred_contact === 'SMS'
        ? this.sendSms(booking.request)
        : this.sendEmail(booking.request);
    })
    .then(( contact : any ) => {
      if ( booking.skipApproval )  {
        return Promise.resolve();
      }

      booking.contact = contact;

      return this.bookingsService.confirm(booking);
    })
    .then(() => {
      this.router.stateService.reload();

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.REQUEST', {
        status: booking.message
      }), 'success');
    })
    .catch(error => this.messagesService.error(error));
  }

  sendEmail ( request : BookingsRequestModel ) : Promise<any> {
    const companyName = this.sessionService.company().company_name;

    return this.messengerService.email({
      body         : this.companyMessages.default_booking_confirmation_message,
      email_address: request.customer_email,
      name         : companyName,
      subject      : `Booking Confirmed for ${
          this.dateParserService.format(request.local_start_time, 'dddd, MMMM Do YYYY')
        } with ${
          companyName
        }`
    }, {
      modalOnly: true,
      factory  : BookingsRequestSendEmailComponent
    });
  }

  sendSms ( request : BookingsRequestModel ) : Promise<any> {
    return this.messengerService.sms({
      message  : this.companyMessages.default_booking_confirmation_sms_message,
      modalOnly: true,
      name     : request.customer_first_name || request.customer_company_name,
      send_to  : request.customer_phone
    }, {
      factory: BookingsRequestSendSmsComponent
    });
  }

  writeAppointmentTypes ( requests : Array<BookingsRequestModel> = this.requests ) : void {
    requests.forEach(( request : BookingsRequestModel ) => {
      if ( request.appointment_type_ids && request.appointment_type_ids.length ) {
        request.appointmentTypes = '';

        for ( let i = 0; i < this.appointmentTypes.length; i++ ) {
          if ( request.appointment_type_ids.indexOf(this.appointmentTypes[ i ].id) !== -1 ) {
            request.appointmentTypes += this.appointmentTypes[ i ].description + ', ';
          }
        }

        request.appointmentTypes = request.appointmentTypes.slice(0, -2);
      }
    });
  }
}